import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useFormatters } from "../components/shared/useFormatters";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Palette,
  CreditCard,
  FileText,
  Save,
  Loader2,
  Image as ImageIcon,
  AlertCircle,
  Eye,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CountrySelect } from "../components/shared/CountrySelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { LoadingSpinner } from "../components/shared/DataComponents";
import api, { extractErrorMessage } from "../lib/api";

// Currency options
const CURRENCIES = [
  { value: "GBP", symbol: "£", label: "British Pound (GBP)" },
  { value: "EUR", symbol: "€", label: "Euro (EUR)" },
  { value: "USD", symbol: "$", label: "US Dollar (USD)" },
  { value: "AUD", symbol: "A$", label: "Australian Dollar (AUD)" },
  { value: "CAD", symbol: "C$", label: "Canadian Dollar (CAD)" },
  { value: "CHF", symbol: "CHF", label: "Swiss Franc (CHF)" },
];

// Date format options
const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (31/12/2025)" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (12/31/2025)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (2025-12-31)" },
  { value: "DD-MM-YYYY", label: "DD-MM-YYYY (31-12-2025)" },
  { value: "DD.MM.YYYY", label: "DD.MM.YYYY (31.12.2025)" },
];

// Tax label options
const TAX_LABELS = [
  { value: "TVA", label: "TVA (Taxe sur la Valeur Ajoutée)" },
  { value: "VAT", label: "VAT (Value Added Tax)" },
  { value: "GST", label: "GST (Goods & Services Tax)" },
  { value: "TAX", label: "TAX (General)" },
  { value: "Sales Tax", label: "Sales Tax" },
  { value: "BTW", label: "BTW (Belasting Toegevoegde Waarde)" },
  { value: "MwSt", label: "MwSt (Mehrwertsteuer)" },
  { value: "IVA", label: "IVA (Impuesto sobre el Valor Añadido)" },
];

export default function CompanyProfilePage() {
  const { formatDate } = useFormatters();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // EVENTSFLO-TENANT-LOGO-FILE-UPLOAD-REGRESSION-FIX-V1
  const [logoUploading, setLogoUploading] = useState(false);
  // EVENTSFLO-PLATFORM-LOGO-DOCUMENT-LOGO-SEPARATION-V1
  const [documentLogoUploading, setDocumentLogoUploading] = useState(false);
  const [formData, setFormData] = useState({
    // Company Identity
    company_name: "",
    legal_name: "",
    registration_number: "",
    vat_number: "",
    // Address
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    // Contact
    contact_email: "",
    contact_phone: "",
    website: "",
    // Branding
    logo_url: "",
    // EVENTSFLO-PLATFORM-LOGO-DOCUMENT-LOGO-SEPARATION-V1
    // A distinct logo used ONLY on Quote/Invoice PDFs. Empty falls back
    // to the Platform logo above on the PDF (backward-compatible).
    document_logo_url: "",
    primary_color: "#14b8a6",
    secondary_color: "#0f172a",
    // Financial Defaults
    currency: "GBP",
    currency_symbol: "£",
    date_format: "DD/MM/YYYY",
    tax_label: "TVA",  // Default to TVA for Europe
    default_tax_rate: 20,
    // Invoice/Quote Content
    invoice_header_text: "",
    invoice_footer_text: "",
    payment_terms: "",
    bank_details_text: "",
    // EVENTSFLO-QUOTE-INVOICE-FOOTER-TEXT-ADMIN-SETTINGS-V1
    quote_invoice_footer_notice: "",
    quote_invoice_footer_right_title: "",
    quote_invoice_footer_right_text: "",
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get("/settings/company");
      if (response.data) {
        setProfileId(response.data.id);
        setFormData({
          company_name: response.data.company_name || "",
          legal_name: response.data.legal_name || "",
          registration_number: response.data.registration_number || "",
          vat_number: response.data.vat_number || "",
          address_line1: response.data.address_line1 || "",
          address_line2: response.data.address_line2 || "",
          city: response.data.city || "",
          postcode: response.data.postcode || "",
          country: response.data.country || "United Kingdom",
          contact_email: response.data.contact_email || "",
          contact_phone: response.data.contact_phone || "",
          website: response.data.website || "",
          logo_url: response.data.logo_url || "",
          document_logo_url: response.data.document_logo_url || "",
          primary_color: response.data.primary_color || "#14b8a6",
          secondary_color: response.data.secondary_color || "#0f172a",
          currency: response.data.currency || "GBP",
          currency_symbol: response.data.currency_symbol || "£",
          date_format: response.data.date_format || "DD/MM/YYYY",
          tax_label: response.data.tax_label || "TVA",
          default_tax_rate: response.data.default_tax_rate ?? 20,
          invoice_header_text: response.data.invoice_header_text || "",
          invoice_footer_text: response.data.invoice_footer_text || "",
          payment_terms: response.data.payment_terms || "",
          bank_details_text: response.data.bank_details_text || "",
          // EVENTSFLO-QUOTE-INVOICE-FOOTER-TEXT-ADMIN-SETTINGS-V1
          quote_invoice_footer_notice:
            response.data.quote_invoice_footer_notice || "",
          quote_invoice_footer_right_title:
            response.data.quote_invoice_footer_right_title || "",
          quote_invoice_footer_right_text:
            response.data.quote_invoice_footer_right_text || "",
        });
      }
    } catch (err) {
      // No profile exists yet, that's fine
      console.log("No company profile found, starting fresh");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCurrencyChange = (currencyCode) => {
    const currency = CURRENCIES.find((c) => c.value === currencyCode);
    setFormData((prev) => ({
      ...prev,
      currency: currencyCode,
      currency_symbol: currency?.symbol || currencyCode,
    }));
  };

  // EVENTSFLO-TENANT-LOGO-FILE-UPLOAD-REGRESSION-FIX-V1
  // EVENTSFLO-PLATFORM-LOGO-DOCUMENT-LOGO-SEPARATION-V1: `target` picks
  // which logo slot (Platform/Login vs Quote/Invoice Document) is written.
  const handleLogoFileSelected = async (file, target = "platform") => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Logo must be JPEG, PNG, WEBP, or GIF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo too large (max 5 MB)");
      return;
    }
    if (!profileId) {
      toast.error("Save the company profile once before uploading a logo");
      return;
    }
    const setBusy = target === "document" ? setDocumentLogoUploading : setLogoUploading;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const response = await api.post(
        `/settings/company/${profileId}/logo?target=${target}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Backend returns both `logo_url` (legacy) and the target-specific key.
      const newUrl =
        target === "document"
          ? response?.data?.document_logo_url
          : response?.data?.logo_url;
      if (newUrl) {
        setFormData((prev) => ({
          ...prev,
          ...(target === "document"
            ? { document_logo_url: newUrl }
            : { logo_url: newUrl }),
        }));
        toast.success(
          target === "document"
            ? "Quote/Invoice document logo uploaded"
            : "Platform logo uploaded"
        );
      } else {
        toast.error("Logo upload returned no URL");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Logo upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleLogoRemove = async (target = "platform") => {
    if (!profileId) {
      setFormData((prev) => ({
        ...prev,
        ...(target === "document"
          ? { document_logo_url: "" }
          : { logo_url: "" }),
      }));
      return;
    }
    const setBusy = target === "document" ? setDocumentLogoUploading : setLogoUploading;
    setBusy(true);
    try {
      await api.delete(`/settings/company/${profileId}/logo?target=${target}`);
      setFormData((prev) => ({
        ...prev,
        ...(target === "document"
          ? { document_logo_url: "" }
          : { logo_url: "" }),
      }));
      toast.success(
        target === "document"
          ? "Quote/Invoice document logo removed"
          : "Platform logo removed"
      );
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Logo remove failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.company_name?.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!formData.address_line1?.trim()) {
      toast.error("Address line 1 is required");
      return;
    }
    if (!formData.city?.trim()) {
      toast.error("City is required");
      return;
    }
    if (!formData.postcode?.trim()) {
      toast.error("Postcode is required");
      return;
    }
    if (!formData.country?.trim()) {
      toast.error("Country is required");
      return;
    }
    if (!formData.contact_email?.trim()) {
      toast.error("Contact email is required");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...formData,
        default_tax_rate: formData.default_tax_rate
          ? parseFloat(formData.default_tax_rate)
          : null,
      };

      if (profileId) {
        // Update existing profile
        await api.patch(`/settings/company/${profileId}`, payload);
        toast.success("Company profile updated successfully");
      } else {
        // Create new profile
        const response = await api.post("/settings/company", payload);
        setProfileId(response.data.id);
        toast.success("Company profile created successfully");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-profile-page">
      {/* Header - EF-SETTINGS-CONSOLIDATION-V1: Updated title and added breadcrumb */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col gap-2">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <a href="/settings" className="hover:text-foreground">Settings</a>
            <span>/</span>
            <span className="text-foreground">Company Profile</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Company Profile</h1>
          <p className="text-sm text-muted-foreground">
            Configure your company identity, branding, and invoice settings
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setPreviewOpen(true)}
          className="shrink-0"
          data-testid="preview-quote-btn"
        >
          <Eye className="h-4 w-4 mr-2" />
          Preview Quote/Invoice
        </Button>
      </div>

      {/* Quote/Invoice Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center justify-between">
              <span>Quote/Invoice Preview</span>
            </DialogTitle>
          </DialogHeader>
          <QuotePreview formData={formData} />
        </DialogContent>
      </Dialog>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section A: Company Identity */}
        <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-white/10">
            <Building2 className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Company Identity</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="company_name"
                data-testid="company-name-input"
                value={formData.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                placeholder="e.g., Your Company Name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="legal_name">Legal Name</Label>
              <Input
                id="legal_name"
                data-testid="legal-name-input"
                value={formData.legal_name}
                onChange={(e) => handleChange("legal_name", e.target.value)}
                placeholder="e.g., Your Company Ltd"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration_number">Registration Number</Label>
              <Input
                id="registration_number"
                data-testid="registration-number-input"
                value={formData.registration_number}
                onChange={(e) =>
                  handleChange("registration_number", e.target.value)
                }
                placeholder="e.g., 12345678"
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vat_number">Tax Number</Label>
              <Input
                id="vat_number"
                data-testid="tax-number-input"
                value={formData.vat_number}
                onChange={(e) => handleChange("vat_number", e.target.value)}
                placeholder="e.g., FR12345678901"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Enter your VAT/TVA/GST number as applicable
              </p>
            </div>
          </div>
        </div>

        {/* Section B: Address & Contact */}
        <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-white/10">
            <MapPin className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Address & Contact</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address_line1">
                Address Line 1 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="address_line1"
                data-testid="address-line1-input"
                value={formData.address_line1}
                onChange={(e) => handleChange("address_line1", e.target.value)}
                placeholder="Street address"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input
                id="address_line2"
                data-testid="address-line2-input"
                value={formData.address_line2}
                onChange={(e) => handleChange("address_line2", e.target.value)}
                placeholder="Suite, unit, floor, etc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">
                City <span className="text-red-500">*</span>
              </Label>
              <Input
                id="city"
                data-testid="city-input"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                placeholder="City"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="postcode">
                Postcode <span className="text-red-500">*</span>
              </Label>
              <Input
                id="postcode"
                data-testid="postcode-input"
                value={formData.postcode}
                onChange={(e) => handleChange("postcode", e.target.value)}
                placeholder="Postcode"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">
                Country <span className="text-red-500">*</span>
              </Label>
              <CountrySelect
                value={formData.country}
                onValueChange={(v) => handleChange("country", v)}
                placeholder="Select country"
                testId="country-select"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">
                Contact Email <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="contact_email"
                  type="email"
                  data-testid="contact-email-input"
                  value={formData.contact_email}
                  onChange={(e) =>
                    handleChange("contact_email", e.target.value)
                  }
                  placeholder="info@company.com"
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="contact_phone"
                  data-testid="contact-phone-input"
                  value={formData.contact_phone}
                  onChange={(e) =>
                    handleChange("contact_phone", e.target.value)
                  }
                  placeholder="+44 123 456 7890"
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="website"
                  data-testid="website-input"
                  value={formData.website}
                  onChange={(e) => handleChange("website", e.target.value)}
                  placeholder="https://www.company.com"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section C: Branding */}
        <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-white/10">
            <Palette className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Branding</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* EVENTSFLO-PLATFORM-LOGO-DOCUMENT-LOGO-SEPARATION-V1
                Two DISTINCT logo slots. Uploading one never touches the
                other. The Platform logo drives Login + in-app header; the
                Document logo drives Quote/Invoice PDFs (with fallback to
                the Platform logo when unset). */}
            <div className="space-y-6">
              {/* --- Platform / Login Logo --- */}
              <div className="space-y-4 rounded-lg border border-white/10 p-4">
                <div className="space-y-1">
                  <Label htmlFor="logo-file-input" className="text-base">
                    Platform / Login Logo
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Shown on the Login page and the in-app header. This is
                    the platform branding — replacing it will <strong>not</strong>{" "}
                    change the logo on your Quote/Invoice PDFs.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    id="logo-file-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    data-testid="platform-logo-file-input"
                    disabled={logoUploading || !profileId}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleLogoFileSelected(f, "platform");
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={logoUploading || !profileId}
                    data-testid="platform-logo-upload-button"
                    onClick={() => {
                      const input = document.getElementById("logo-file-input");
                      if (input) input.click();
                    }}
                  >
                    {logoUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Choose Platform Logo
                      </>
                    )}
                  </Button>
                  {formData.logo_url ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={logoUploading}
                      data-testid="platform-logo-remove-button"
                      onClick={() => handleLogoRemove("platform")}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, WEBP or GIF · max 5 MB · recommended 200×60px
                  {!profileId
                    ? " · save the profile once before uploading"
                    : ""}
                </p>

                <div className="space-y-2">
                  <Label htmlFor="logo_url" className="text-sm">
                    Platform Logo URL (advanced)
                  </Label>
                  <div className="relative">
                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="logo_url"
                      data-testid="platform-logo-url-input"
                      value={formData.logo_url}
                      onChange={(e) => handleChange("logo_url", e.target.value)}
                      placeholder="https://example.com/logo.png"
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Optional — paste a hosted URL instead of uploading a file.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Platform Logo Preview</Label>
                  <div
                    className="border border-dashed border-white/20 rounded-lg p-4 min-h-[100px] flex items-center justify-center bg-white/5"
                    data-testid="platform-logo-preview"
                  >
                    {formData.logo_url ? (
                      <img
                        src={formData.logo_url}
                        alt="Platform logo preview"
                        className="max-h-20 max-w-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`flex flex-col items-center gap-2 text-muted-foreground ${
                        formData.logo_url ? "hidden" : "flex"
                      }`}
                    >
                      <ImageIcon className="h-8 w-8" />
                      <span className="text-sm">No platform logo configured</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* --- Quote / Invoice Document Logo --- */}
              <div className="space-y-4 rounded-lg border border-white/10 p-4">
                <div className="space-y-1">
                  <Label
                    htmlFor="document-logo-file-input"
                    className="text-base"
                  >
                    Quote / Invoice Document Logo
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Used exclusively at the top of Quote and Invoice PDFs.
                    Replacing it will <strong>not</strong> change the Platform /
                    Login logo above. If left empty, PDFs fall back to the
                    Platform logo.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    id="document-logo-file-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    data-testid="document-logo-file-input"
                    disabled={documentLogoUploading || !profileId}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleLogoFileSelected(f, "document");
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={documentLogoUploading || !profileId}
                    data-testid="document-logo-upload-button"
                    onClick={() => {
                      const input = document.getElementById(
                        "document-logo-file-input"
                      );
                      if (input) input.click();
                    }}
                  >
                    {documentLogoUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Choose Document Logo
                      </>
                    )}
                  </Button>
                  {formData.document_logo_url ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={documentLogoUploading}
                      data-testid="document-logo-remove-button"
                      onClick={() => handleLogoRemove("document")}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, WEBP or GIF · max 5 MB · recommended 200×60px
                  {!profileId
                    ? " · save the profile once before uploading"
                    : ""}
                </p>

                <div className="space-y-2">
                  <Label htmlFor="document_logo_url" className="text-sm">
                    Document Logo URL (advanced)
                  </Label>
                  <div className="relative">
                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="document_logo_url"
                      data-testid="document-logo-url-input"
                      value={formData.document_logo_url}
                      onChange={(e) =>
                        handleChange("document_logo_url", e.target.value)
                      }
                      placeholder="https://example.com/document-logo.png"
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Optional — paste a hosted URL instead of uploading a file.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Document Logo Preview</Label>
                  <div
                    className="border border-dashed border-white/20 rounded-lg p-4 min-h-[100px] flex items-center justify-center bg-white/5"
                    data-testid="document-logo-preview"
                  >
                    {formData.document_logo_url ? (
                      <img
                        src={formData.document_logo_url}
                        alt="Quote/Invoice document logo preview"
                        className="max-h-20 max-w-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`flex flex-col items-center gap-2 text-muted-foreground ${
                        formData.document_logo_url ? "hidden" : "flex"
                      }`}
                    >
                      <ImageIcon className="h-8 w-8" />
                      <span className="text-sm">
                        No document logo — PDFs will use the Platform logo
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Color Pickers */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="primary_color">Primary Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="primary_color"
                    data-testid="primary-color-input"
                    value={formData.primary_color || "#14b8a6"}
                    onChange={(e) =>
                      handleChange("primary_color", e.target.value)
                    }
                    className="w-12 h-10 rounded border border-white/20 cursor-pointer"
                  />
                  <Input
                    value={formData.primary_color || "#14b8a6"}
                    onChange={(e) =>
                      handleChange("primary_color", e.target.value)
                    }
                    placeholder="#14b8a6"
                    className="font-mono flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Used for headers and accents on quotes/invoices
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="secondary_color">Secondary Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    id="secondary_color"
                    data-testid="secondary-color-input"
                    value={formData.secondary_color || "#0f172a"}
                    onChange={(e) =>
                      handleChange("secondary_color", e.target.value)
                    }
                    className="w-12 h-10 rounded border border-white/20 cursor-pointer"
                  />
                  <Input
                    value={formData.secondary_color || "#0f172a"}
                    onChange={(e) =>
                      handleChange("secondary_color", e.target.value)
                    }
                    placeholder="#0f172a"
                    className="font-mono flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Used for secondary elements and text
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section D: Financial Defaults */}
        <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-white/10">
            <CreditCard className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Financial Defaults</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={handleCurrencyChange}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency_symbol">Currency Symbol</Label>
              <Input
                id="currency_symbol"
                data-testid="currency-symbol-input"
                value={formData.currency_symbol}
                onChange={(e) =>
                  handleChange("currency_symbol", e.target.value)
                }
                placeholder="£"
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_format">Date Format</Label>
              <Select
                value={formData.date_format}
                onValueChange={(v) => handleChange("date_format", v)}
              >
                <SelectTrigger data-testid="date-format-select">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMATS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_label">Tax Label</Label>
              <Select
                value={formData.tax_label}
                onValueChange={(v) => handleChange("tax_label", v)}
              >
                <SelectTrigger data-testid="tax-label-select">
                  <SelectValue placeholder="Select label" />
                </SelectTrigger>
                <SelectContent>
                  {TAX_LABELS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default_tax_rate">Default Tax Rate (%)</Label>
              <Input
                id="default_tax_rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                data-testid="default-tax-rate-input"
                value={formData.default_tax_rate ?? ""}
                onChange={(e) =>
                  handleChange("default_tax_rate", e.target.value)
                }
                placeholder="20"
                className="font-mono"
              />
            </div>
          </div>
        </div>

        {/* Section E: Invoice & Quote Content */}
        <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-white/10">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Invoice & Quote Content</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoice_header_text">Header Text</Label>
              <Textarea
                id="invoice_header_text"
                data-testid="invoice-header-text-input"
                value={formData.invoice_header_text}
                onChange={(e) =>
                  handleChange("invoice_header_text", e.target.value)
                }
                placeholder="Text to appear at the top of invoices and quotes..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice_footer_text">Footer Text</Label>
              <Textarea
                id="invoice_footer_text"
                data-testid="invoice-footer-text-input"
                value={formData.invoice_footer_text}
                onChange={(e) =>
                  handleChange("invoice_footer_text", e.target.value)
                }
                placeholder="Text to appear at the bottom of invoices and quotes..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_terms">Payment Terms</Label>
              <Textarea
                id="payment_terms"
                data-testid="payment-terms-input"
                value={formData.payment_terms}
                onChange={(e) => handleChange("payment_terms", e.target.value)}
                placeholder="e.g., Payment due within 30 days of invoice date..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_details_text">Bank / Payment Details</Label>
              <Textarea
                id="bank_details_text"
                data-testid="bank-details-text-input"
                value={formData.bank_details_text}
                onChange={(e) =>
                  handleChange("bank_details_text", e.target.value)
                }
                placeholder="Bank: Example Bank&#10;Sort Code: 12-34-56&#10;Account: 12345678"
                rows={3}
              />
            </div>

            {/* EVENTSFLO-QUOTE-INVOICE-FOOTER-TEXT-ADMIN-SETTINGS-V1
                Quote / Invoice Footer extras — notice line + right-side
                tenant-defined text. Total/VAT values must NOT be entered
                here; they continue to be computed and rendered above. */}
            <div className="space-y-2">
              <Label htmlFor="quote_invoice_footer_notice">
                Footer Notice Line
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  (rendered in red above bank details)
                </span>
              </Label>
              <Textarea
                id="quote_invoice_footer_notice"
                data-testid="footer-notice-input"
                value={formData.quote_invoice_footer_notice}
                onChange={(e) =>
                  handleChange(
                    "quote_invoice_footer_notice",
                    e.target.value,
                  )
                }
                placeholder="e.g., PLEASE NOTE OUR UPDATED BANK DETAILS AND ADDRESS BELOW"
                rows={2}
                maxLength={500}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote_invoice_footer_right_title">
                Footer Right-side Title
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  (optional, e.g. &quot;Notes&quot;)
                </span>
              </Label>
              <input
                id="quote_invoice_footer_right_title"
                data-testid="footer-right-title-input"
                value={formData.quote_invoice_footer_right_title}
                onChange={(e) =>
                  handleChange(
                    "quote_invoice_footer_right_title",
                    e.target.value,
                  )
                }
                placeholder="Notes"
                maxLength={120}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote_invoice_footer_right_text">
                Footer Right-side Text
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  (tenant-defined notes; do NOT enter calculated totals/VAT)
                </span>
              </Label>
              <Textarea
                id="quote_invoice_footer_right_text"
                data-testid="footer-right-text-input"
                value={formData.quote_invoice_footer_right_text}
                onChange={(e) =>
                  handleChange(
                    "quote_invoice_footer_right_text",
                    e.target.value,
                  )
                }
                placeholder="Short legal notes, payment reminders, operating notes, etc."
                rows={4}
                maxLength={1500}
              />
            </div>
          </div>
        </div>

        {/* Import Notice */}
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-yellow-500">Import Ready</p>
            <p className="text-muted-foreground mt-1">
              This form supports partial data entry for uTrac import
              compatibility. Required fields are marked with{" "}
              <span className="text-red-500">*</span>. Other fields can be
              completed later.
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t border-white/10">
          <Button
            type="submit"
            disabled={saving}
            className="bg-primary hover:bg-primary/90 min-w-[150px]"
            data-testid="save-company-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {profileId ? "Save Changes" : "Create Profile"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Quote/Invoice Preview Component
function QuotePreview({ formData }) {
  const { formatDate } = useFormatters();
  // Sample quote data for preview - use static dates for purity
  const today = new Date();
  const validUntil = new Date(today);
  validUntil.setDate(validUntil.getDate() + 30);
  
  const sampleQuote = {
    quote_number: "QT-000123",
    issue_date: formatDate(today.toISOString()),
    valid_until: formatDate(validUntil.toISOString()),
    client_name: "Sample Client Ltd",
    client_email: "client@example.com",
    client_address: "123 Client Street\nLondon, EC1A 1BB",
    project_name: "Corporate Event 2025",
    lines: [
      { description: "Event Crew - Day Shift (8 hours)", quantity: 10, unit_price: 150 },
      { description: "Supervisor - Day Shift (8 hours)", quantity: 2, unit_price: 200 },
      { description: "Meal Allowance", quantity: 12, unit_price: 15 },
    ],
  };

  const taxRate = formData.default_tax_rate || 20;
  const subtotal = sampleQuote.lines.reduce((sum, line) => sum + line.quantity * line.unit_price, 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;
  const currencySymbol = formData.currency_symbol || "£";

  return (
    <div className="bg-white text-foreground rounded-b-lg overflow-hidden" data-testid="quote-preview">
      {/* Header with Company Branding */}
      <div 
        className="p-6"
        style={{ backgroundColor: formData.primary_color || "#0f172a" }}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="text-white">
            {/* EVENTSFLO-PLATFORM-LOGO-DOCUMENT-LOGO-SEPARATION-V1
                Preview mirrors the PDF renderer: prefer document logo,
                fall back to platform logo. */}
            {(formData.document_logo_url || formData.logo_url) ? (
              <img 
                src={formData.document_logo_url || formData.logo_url} 
                alt={formData.company_name || "Company Logo"}
                className="h-12 max-w-[200px] object-contain mb-2"
              />
            ) : (
              <h1 className="text-2xl font-bold tracking-tight">
                {formData.company_name || "YOUR COMPANY NAME"}
              </h1>
            )}
            <div className="text-white/80 text-sm mt-2 space-y-0.5">
              {formData.address_line1 && (
                <p>{formData.address_line1}{formData.address_line2 ? `, ${formData.address_line2}` : ""}</p>
              )}
              {(formData.city || formData.postcode) && (
                <p>{[formData.city, formData.postcode].filter(Boolean).join(", ")}</p>
              )}
              {formData.country && <p>{formData.country}</p>}
              <div className="flex flex-wrap gap-4 mt-2 text-xs">
                {formData.contact_phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {formData.contact_phone}
                  </span>
                )}
                {formData.contact_email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {formData.contact_email}
                  </span>
                )}
                {formData.website && (
                  <span className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    {formData.website}
                  </span>
                )}
              </div>
              {formData.vat_number && (
                <p className="text-xs mt-1">{formData.tax_label || "TVA"}: {formData.vat_number}</p>
              )}
            </div>
          </div>
          <div className="text-right text-white">
            <span className="text-2xl font-bold">QUOTE</span>
            <p 
              className="text-xl font-mono mt-1"
              style={{ color: formData.secondary_color ? `${formData.secondary_color}` : "#5eead4" }}
            >
              {sampleQuote.quote_number}
            </p>
            <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded bg-yellow-500/20 text-yellow-300">
              DRAFT
            </span>
          </div>
        </div>
        
        {/* Header Text */}
        {formData.invoice_header_text && (
          <div className="mt-4 pt-4 border-t border-white/20 text-sm text-white/80">
            {formData.invoice_header_text}
          </div>
        )}
      </div>

      {/* Quote Info & Client Details */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-border">
        <div>
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Quote Details
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Issue Date:</span>
              <span className="font-medium">{sampleQuote.issue_date}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Valid Until:</span>
              <span className="font-medium">{sampleQuote.valid_until}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Project:</span>
              <span className="font-medium">{sampleQuote.project_name}</span>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Bill To
          </h2>
          <div className="space-y-1">
            <p className="font-semibold text-foreground">{sampleQuote.client_name}</p>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{sampleQuote.client_address}</p>
            <p className="text-sm text-muted-foreground">{sampleQuote.client_email}</p>
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="p-6">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Line Items
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Description
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-24">
                  Qty
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-32">
                  Unit Price
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-32">
                  Line Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sampleQuote.lines.map((line, index) => (
                <tr key={index} className="hover:bg-surface-elevated">
                  <td className="py-3 px-4 text-sm text-foreground">{line.description}</td>
                  <td className="py-3 px-4 text-sm text-foreground text-right font-mono">{line.quantity}</td>
                  <td className="py-3 px-4 text-sm text-foreground text-right font-mono">
                    {currencySymbol}{line.unit_price.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground text-right font-mono font-medium">
                    {currencySymbol}{(line.quantity * line.unit_price).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Section */}
        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-2">
            <div className="flex justify-between py-2 text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono font-medium">{currencySymbol}{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 text-sm border-b border-border">
              <span className="text-muted-foreground">{formData.tax_label || "Tax"} ({taxRate}%)</span>
              <span className="font-mono font-medium">{currencySymbol}{taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 text-lg font-bold">
              <span className="text-foreground">Total</span>
              <span 
                className="font-mono"
                style={{ color: formData.primary_color || "#0d9488" }}
              >
                {currencySymbol}{total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      {formData.payment_terms && (
        <div className="p-6 border-t border-border bg-surface-elevated">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Payment Terms
          </h2>
          <p className="text-sm text-foreground whitespace-pre-line">{formData.payment_terms}</p>
        </div>
      )}

      {/* Bank Details */}
      {formData.bank_details_text && (
        <div className="p-6 border-t border-border bg-surface-elevated">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Payment Details
          </h2>
          <p className="text-sm text-foreground whitespace-pre-line font-mono">{formData.bank_details_text}</p>
        </div>
      )}

      {/* Footer */}
      <div className="p-6 border-t border-border bg-surface-elevated">
        <div className="text-center text-xs text-muted-foreground">
          {formData.invoice_footer_text ? (
            <p className="whitespace-pre-line">{formData.invoice_footer_text}</p>
          ) : (
            <p>Thank you for your business!</p>
          )}
        </div>
      </div>

      {/* Preview Notice */}
      <div className="p-4 bg-blue-50 border-t border-blue-200 text-center">
        <p className="text-xs text-blue-600">
          This is a preview using sample data. Your actual quotes and invoices will display real client and line item information.
        </p>
      </div>
    </div>
  );
}
